import { SQLiteObject } from "@awesome-cordova-plugins/sqlite";
import ISQLiteResponse from "../interfaces/ISQLiteResponse";
import { DefaultReadingPlanRecord } from "./DefaultReadingPlanRecord";
import { SQLiteTables } from "./SQLiteTables";
import SQLiteHelperService from "../services/SQLiteHelperService";
import IFellowshipSettings from "../interfaces/IFellowshipSettings";
import IReadingPlanRecord from "../interfaces/IReadingPlanRecord";
import ReadingPlanRecordService from "../services/ReadingPlanRecordService";

interface ISQLiteDataMigrations {
    [key:number] :(db: SQLiteObject) => {
        preTableMigration: () => Promise<any>;
        postTableMigration: () => Promise<any>;
    }
}

/// ----- *** --------------- *** -----
/// ----- ***    DB VERSION   *** -----
/// ----- *** --------------- *** -----
export const CurrentSQLiteDbVersion = 7;


/// ----- *** --------------- *** -----
/// ----- *** TABLE MIGRATIONS *** -----
/// ----- *** --------------- *** -----
export const SQLiteTableMigrations: {[key:number] : string[]} = {
    1: [
        "CREATE TABLE IF NOT EXISTS version (number INTEGER)",
        "INSERT INTO version VALUES (1)"
    ],
    2: [
        "UPDATE version SET number = 2 WHERE number = 1",
        "CREATE TABLE IF NOT EXISTS prayers (id INTEGER PRIMARY KEY NOT NULL, title TEXT, description TEXT, answer TEXT, category TEXT, status TEXT, note TEXT, 'order' INTEGER, createdBy TEXT, createdDate NUMERIC, modifiedDate NUMERIC, notActiveDate NUMERIC, linkedPostId TEXT, syncId INTEGER)",
        "CREATE TABLE IF NOT EXISTS prayerSettings (id INTEGER PRIMARY KEY NOT NULL, timed BOOLEAN, minimumPrayerTime INTEGER, sessionTime INTEGER, shuffle BOOLEAN, alert BOOLEAN, showSettings BOOLEAN, nightMode BOOLEAN)",
        "CREATE TABLE IF NOT EXISTS readingPlanRecords (id INTEGER PRIMARY KEY NOT NULL, readingPlan TEXT, active BOOLEAN, startDate NUMERIC, sessionsPerDay INTEGER, completedSessions TEXT, modifiedDate NUMERIC, syncId INTEGER)",
        "CREATE TABLE IF NOT EXISTS dismissibleMessages (id INTEGER PRIMARY KEY NOT NULL, messageId TEXT, category TEXT, timesDisplayed INTEGER, dateDisplayed NUMERIC, dismissed BOOLEAN)"
    ],
    3: [
        "UPDATE version SET number = 3 WHERE number = 2",
        "ALTER TABLE readingPlanRecords ADD COLUMN sessionsURL TEXT",
        "ALTER TABLE readingPlanRecords ADD COLUMN sessionsType TEXT",
        "ALTER TABLE readingPlanRecords ADD COLUMN readingPlanCode TEXT",
        "ALTER TABLE readingPlanRecords ADD COLUMN readingPlanExtrasLink TEXT"
    ],
    4: [
        "UPDATE version SET number = 4 WHERE number = 3",
        "CREATE TABLE IF NOT EXISTS bibleSettings (id INTEGER PRIMARY KEY NOT NULL, defaultVersion TEXT, fontSize INTEGER, font TEXT, audioSpeed REAL, audioScroll BOOLEAN, tiltScroll BOOLEAN, colorTheme TEXT, strongs BOOLEAN, morphology BOOLEAN, footnotes BOOLEAN, crossReferences BOOLEAN, screenOn BOOLEAN, hideMenu TEXT)",
        "ALTER TABLE prayerSettings ADD COLUMN hapticAlert BOOLEAN",
    ],
    5: [
        "UPDATE version SET number = 5 WHERE number = 4",
        "CREATE TABLE IF NOT EXISTS fellowshipSettings (id INTEGER PRIMARY KEY NOT NULL, defaultGroupId TEXT, nightMode BOOLEAN, userFellowshipGroups TEXT)",
        "CREATE TABLE IF NOT EXISTS fellowshipPosts (id TEXT PRIMARY KEY NOT NULL, userId TEXT, groupId TEXT, linkedPostId TEXT, type INTEGER, author TEXT, title TEXT, postText Text, prayingTotal INTEGER, commentsTotal INTEGER, likesTotal INTEGER, prayingUserIds TEXT, likesUserIds TEXT, comments TEXT, createdDate NUMERIC, modifiedDate NUMERIC, sortDate NUMERIC, subscribed BOOLEAN, hidden BOOLEAN)",
    ],
    6: [
        "UPDATE version SET number = 6 WHERE number = 5",
        "ALTER TABLE readingPlanRecords ADD COLUMN groupId TEXT",
        "ALTER TABLE readingPlanRecords ADD COLUMN name TEXT",
        "ALTER TABLE bibleSettings ADD COLUMN activeReadingPlanId INTEGER",
    ],
    7: [
        "UPDATE version SET number = 7 WHERE number = 6",
        "ALTER TABLE readingPlanRecords ADD COLUMN lastFreeStylePassage TEXT",
    ],
}

/// ----- *** ------------------------- *** -----
/// ----- *** DATA MIGRATIONS FUNCTIONS *** -----
/// ----- *** ------------------------- *** -----

// VERSION 2: DATA MIGRATION
const dataMigration_2 = (db:SQLiteObject) => {
    const preTableMigration = async () => {
        const selectQuery = 'SELECT * FROM bst';
        preMigrationData = await db.executeSql(selectQuery, []).then((result: ISQLiteResponse<any>) => {
            if(result.rows.length) {
                return result.rows.item(0);
            } else {
                return undefined;
            }
        }).catch(error => console.error('Error PreMigrateData: ', error));
        console.log('PreMigrateData: ', JSON.stringify(preMigrationData));
    }
    const postTableMigration = async () => {
        if(preMigrationData) {
            // manipulate preMigrationData and then input it into the new table
            let completedSessions = JSON.parse(preMigrationData.planRead).map((read: number) => !!read).toString();
            completedSessions = `[${completedSessions}]`;
            const query = `INSERT INTO readingPlanRecords (readingPlan, active, startDate, sessionsPerDay, completedSessions, modifiedDate) VALUES ('chronological-cross-reference', true,  ${preMigrationData.planDate}, 1, '${completedSessions}', ${Date.now()})`;
            console.log('PostMigrateData query:', query);
            return await db.executeSql(query).then((result: ISQLiteResponse<any>) => console.log('DATA MIGRATED: ', query))
                .catch (error => console.error('Error PostMigrateData: ', JSON.stringify(error), query));
        } else { console.log('NO migration data retrieved in pre-migration function.') }
    }
    // preMigrationData is a stored using preTableMigration() and then used by postTableMigration() update db records
    let preMigrationData: any = undefined;
    return { preTableMigration, postTableMigration}
}

// VERSION 3: DATA MIGRATION
const dataMigration_3 = (db:SQLiteObject) => {
    const preTableMigration = async () => {
        const selectQuery = 'SELECT * FROM readingPlanRecords';
        preMigrationData = await db.executeSql(selectQuery, []).then((result: ISQLiteResponse<any>) => {
            if(result.rows.length) {
                return result.rows.item(0);
            } else {
                return undefined;
            }
        }).catch(error => console.error('Error PreMigrateData: ', error));
        console.log('PreMigrateData: ', JSON.stringify(preMigrationData));
    }
    const postTableMigration = async () => {
        if(preMigrationData) {
            // if there are any reading plan record tables update them with default db values;
            const query = `UPDATE readingPlanRecords SET sessionsURL='${DefaultReadingPlanRecord.sessionsURL}', sessionsType='${DefaultReadingPlanRecord.sessionsType}', readingPlanCode='${DefaultReadingPlanRecord.readingPlanCode}', readingPlanExtrasLink='${DefaultReadingPlanRecord.readingPlanExtrasLink}', readingPlan='${DefaultReadingPlanRecord.readingPlan}'`;
            console.log('PostMigrateData query:', query);
            return await db.executeSql(query, [])
                .then((result: ISQLiteResponse<any>) => console.log('DATA MIGRATED: ', query))
                .catch (error => console.error('Error PostMigrateData: ', JSON.stringify(error), query));
        } else { console.log('NO migration data retrieved in pre-migration function.') }
    }
    // preMigrationData is a stored using preTableMigration() and then used by postTableMigration() update db records
    let preMigrationData: any = undefined;
    return { preTableMigration, postTableMigration}
}

// VERSION 4: DATA MIGRATION
const dataMigration_4 = (db:SQLiteObject) => {
    const preTableMigration = async () => {}
    const postTableMigration = async () => {
        // Default Audio Auto Scroll to true;
        const query = `UPDATE bibleSettings SET audioScroll=true`;
        console.log('PostMigrateData query:', query);
        return await db.executeSql(query, [])
            .then((result: ISQLiteResponse<any>) => console.log('DATA MIGRATED: ', query))
            .catch (error => console.error('Error PostMigrateData: ', JSON.stringify(error), query));
    }
    return { preTableMigration, postTableMigration}
}

// VERSION 6: DATA MIGRATION
const dataMigration_6 = (db:SQLiteObject) => {
    const preTableMigration = async () => {}
    const postTableMigration = async () => {
        // Get FellowshipSettings
        const query1 = `SELECT * FROM ${SQLiteTables.fellowshipSettings} WHERE \`id\`=1`;
        console.log('PostMigrateData query:', query1);
        const fellowshipSettings = await db.executeSql(query1, [])
            .then((result:ISQLiteResponse<IFellowshipSettings>)=>{
                if(result.rows.length) {
                    return SQLiteHelperService.convertToJsTypes(result.rows.item(0)) as IFellowshipSettings;
                }
            })
            .catch (error => console.error('Error PostMigrateData: ', JSON.stringify(error), query1));

        // Get reading plan record
        const queryGetRecord = `SELECT * FROM ${SQLiteTables.readingPlanRecords} WHERE \`id\`=1`;
        console.log('PostMigrateData query:', queryGetRecord);
        const record = await db.executeSql(queryGetRecord, [])
            .then((result:ISQLiteResponse<IReadingPlanRecord>)=>{
                if(result.rows.length) {
                    return SQLiteHelperService.convertToJsTypes(result.rows.item(0)) as IReadingPlanRecord;
                }
            })
            .catch (error => console.error('Error PostMigrateData: ', JSON.stringify(error), queryGetRecord));

        let queryUpdateReadingPlanRecord = '';
        const userGroup = fellowshipSettings?.defaultGroupId ? fellowshipSettings.userFellowshipGroups[fellowshipSettings.defaultGroupId].group: undefined;

        if(userGroup?.syncCode && record) {
            const result = ReadingPlanRecordService.interpretSyncCode!(userGroup.syncCode);
            const millisecondsPerDay = 86400000;
            const startDateDifference = !!result ? Math.abs(result.startDate.getTime() - record.startDate.getTime()) : millisecondsPerDay + 10;

            // make it so the users reading plan is becomes there group reading plan
            if(record?.readingPlanCode === result?.readingPlanCode && startDateDifference < millisecondsPerDay) {
                // There should only be one readingPlanRecord so update them all for simplicity
                queryUpdateReadingPlanRecord = `UPDATE ${SQLiteTables.readingPlanRecords} SET groupId='${userGroup.id}', name='${userGroup.name}'`;
            } else {
                // There should only be one readingPlanRecord so update them all for simplicity
                queryUpdateReadingPlanRecord = `UPDATE ${SQLiteTables.readingPlanRecords} SET groupId='', name='My Reading Plan'`;
            }
        } else {
            // There should only be one readingPlanRecord so update them all for simplicity
            queryUpdateReadingPlanRecord = `UPDATE ${SQLiteTables.readingPlanRecords} SET groupId='', name='My Reading Plan'`;
        }

        console.log('PostMigrateData query:', queryUpdateReadingPlanRecord);
        
        await db.executeSql(queryUpdateReadingPlanRecord, [])
            .then((result: ISQLiteResponse<any>) => console.log('DATA MIGRATED: ', queryUpdateReadingPlanRecord))
            .catch (error => console.error('Error PostMigrateData: ', JSON.stringify(error), queryUpdateReadingPlanRecord));


        // Set activeReadingPlanId to 1 because this is the only current reading plan
        const query = `UPDATE bibleSettings SET activeReadingPlanId=1`;
        console.log('PostMigrateData query:', query);
        return await db.executeSql(query, [])
            .then((result: ISQLiteResponse<any>) => console.log('DATA MIGRATED: ', query))
            .catch (error => console.error('Error PostMigrateData: ', JSON.stringify(error), query));
    }
    return { preTableMigration, postTableMigration}
}

/// ----- *** --------------- *** -----
/// ----- *** DATA MIGRATIONS *** -----
/// ----- *** --------------- *** -----
export const SQLiteDataMigrations: ISQLiteDataMigrations = {
    2 : dataMigration_2,
    3 : dataMigration_3,
    4 : dataMigration_4,
    6 : dataMigration_6,
}
